import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import styled from "styled-components/macro";
import { DocumentPreview } from "../../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/DocumentPreview";
import { endpoints } from "../../../endpoints";
import type {
  Assets,
  AttributeSchema,
  DocumentAttributeValue,
  GridItemProps,
  SupportedAssetType,
} from "../../../types/types.PIM";
import { useStoreState } from "../../../util/util";
import { useAuthContext } from "../../Auth";
import {
  GetDocumentIcon,
  get_content_type,
} from "../../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import { LinkIcon } from "../../Icons/Icons";
import { TextMdRegular } from "../../Typography/TypographyV2";
import { Chip } from "../../Chips/Chips";

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

export const DocumentTag = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.resolve("color_border_light")};
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  overflow: hidden;
`;

export const DocumentAttributePreview = ({
  attribute,
  maxVisible,
  onMoreClick,
}: {
  attribute: AttributeSchema;
  /** When set, only the first `maxVisible` tags are shown and a "+N more" chip appears. */
  maxVisible?: number;
  /** Called when the "+N more" chip is clicked. Receives all attribute values. */
  onMoreClick?: (values: AttributeSchema["values"]) => void;
}) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { roleIsGuest, roleIsSomeKindOfBuyer } = useAuthContext();
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const { data: previewAsset } = useSWR<Assets>(
    selectedAssetId
      ? endpoints.v2_tenants_id_pim_assets_id(tenant_id, selectedAssetId)
      : null,
    { revalidateOnFocus: false }
  );

  const values = useMemo(() => {
    if (!attribute.values) return [];
    return attribute.values.map((val) => {
      const { asset_name, asset_type, content_type, host } = val;
      const original = val.value as DocumentAttributeValue;
      return {
        ...val,
        value: {
          ...original,
          display_name: asset_name ?? original.display_name,
          is_external:
            host !== undefined
              ? !!host && host.name !== "Agilis"
              : original.is_external ?? false,
          asset_type: asset_type ?? original.asset_type,
          content_type: content_type ?? original.content_type,
          host_name: host?.name ?? original.host_name,
        },
      };
    });
  }, [attribute.values]);

  if (!values.length) return <></>;

  const handleTagClick = (docValue: DocumentAttributeValue, url?: string) => {
    if (docValue.is_external && url) {
      window.open(url, "_blank", "noopener noreferrer");
    } else {
      setSelectedAssetId(docValue.asset_id);
    }
  };

  const assetForPreview: (Assets & GridItemProps) | null = previewAsset
    ? {
        ...previewAsset,
        can_edit: false,
        can_change_visibility: false,
        can_delete: false,
        can_external_download: roleIsGuest
          ? previewAsset.visibility === "public"
          : !roleIsSomeKindOfBuyer || previewAsset.visibility !== "internal",
        can_view_details: true,
      }
    : null;

  const visibleValues =
    maxVisible !== undefined ? values.slice(0, maxVisible) : values;
  const hiddenCount = values.length - visibleValues.length;

  return (
    <>
      <TagsContainer>
        {visibleValues.map((attrValue) => {
          const docValue = attrValue.value as DocumentAttributeValue;
          const url =
            attrValue.cdn_url || attrValue.signed_url || attrValue.preview_url;
          const label = docValue.display_name || t("Document");

          return (
            <DocumentTag
              key={docValue.asset_id}
              onClick={() => handleTagClick(docValue, url)}
            >
              <IconWrapper>
                {docValue.is_external ? (
                  <LinkIcon width={16} height={16} />
                ) : (
                  <GetDocumentIcon
                    asset_type={
                      docValue.asset_type
                        ? (docValue.asset_type as SupportedAssetType)
                        : "document"
                    }
                    file_type={get_content_type(
                      docValue.content_type ?? "application/pdf"
                    )}
                  />
                )}
              </IconWrapper>
              <TextMdRegular
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "200px",
                }}
                color="secondary"
              >
                {label}
              </TextMdRegular>
            </DocumentTag>
          );
        })}
        {hiddenCount > 0 && (
          <Chip onClick={() => onMoreClick?.(values)}>
            <TextMdRegular color="secondary">{`+${hiddenCount} ${t(
              "more"
            )}`}</TextMdRegular>
          </Chip>
        )}
      </TagsContainer>
      {assetForPreview && (
        <DocumentPreview
          assets={[assetForPreview]}
          selected_asset_idx={0}
          onCancelPreview={() => setSelectedAssetId(null)}
        />
      )}
    </>
  );
};
